/**
 * Community brand badges — the client half of pds-pro's Brand Manager badges.
 *
 * The operator defines badges and grants them (or Stripe grants them on
 * checkout); the public brand document carries defs + a DID→slugs holder map,
 * which `#/lib/brand/runtime` validates once at startup. Here we just draw what
 * a DID holds.
 *
 * Deployments with no badges (every brand without a `brandConfigUrl`) render
 * nothing at all — `badgesForDid` returns a shared empty array and each
 * component bails before any hook does work.
 */

import {View} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'

import {badgePurchaseUrl, badgesForDid} from '#/lib/brand/badges'
import {type BrandBadgeDef} from '#/lib/brand/types'
import {useOpenLink} from '#/lib/hooks/useOpenLink'
import {useSession} from '#/state/session'
import {atoms as a, useTheme, web} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {useDialogControl} from '#/components/Dialog'
import {Text} from '#/components/Typography'

/**
 * The badge's own mark: its emoji, or a tinted dot when the operator set no
 * icon. The dot keeps the row's rhythm intact instead of collapsing the badge
 * to nothing — a granted badge should always be visible.
 */
function BadgeMark({def, width}: {def: BrandBadgeDef; width: number}) {
  const t = useTheme()

  if (def.icon) {
    return (
      <Text
        emoji
        allowFontScaling={false}
        style={[{fontSize: width, lineHeight: width * 1.1}]}>
        {def.icon}
      </Text>
    )
  }
  return (
    <View
      style={[
        a.rounded_full,
        {
          width: width * 0.7,
          height: width * 0.7,
          backgroundColor: def.color || t.palette.primary_500,
        },
      ]}
    />
  )
}

/** Non-interactive marks, for dense surfaces (list rows, post bylines). */
export function BrandBadgeMarks({
  did,
  width,
}: {
  did: string | undefined
  width: number
}) {
  const badges = badgesForDid(did)
  if (badges.length === 0) return null
  return (
    <>
      {badges.map(def => (
        <BadgeMark key={def.slug} def={def} width={width} />
      ))}
    </>
  )
}

/** Tappable marks that open the detail dialog. Used on the profile header. */
export function BrandBadgeButtons({
  did,
  width,
}: {
  did: string | undefined
  width: number
}) {
  const badges = badgesForDid(did)
  if (badges.length === 0) return null
  return (
    <>
      {badges.map(def => (
        <BrandBadgeButton key={def.slug} def={def} width={width} />
      ))}
    </>
  )
}

function BrandBadgeButton({def, width}: {def: BrandBadgeDef; width: number}) {
  const {t: l} = useLingui()
  const control = useDialogControl()

  return (
    <>
      <Button
        label={def.label}
        hitSlop={20}
        onPress={evt => {
          evt.preventDefault()
          control.open()
        }}>
        {({hovered}) => (
          <View
            style={[
              a.justify_center,
              a.align_center,
              a.transition_transform,
              {
                width,
                height: width,
                transform: [{scale: hovered ? 1.1 : 1}],
              },
            ]}>
            <BadgeMark def={def} width={width} />
          </View>
        )}
      </Button>
      <BrandBadgeDialog control={control} def={def} label={l`Badge`} />
    </>
  )
}

/**
 * Badge detail: what it is, and — when the VIEWER doesn't hold a purchasable
 * badge — a way to buy it.
 *
 * The purchase CTA is deliberately gated on the viewer, not the profile being
 * looked at: checkout is credited to whoever is signed in
 * (`client_reference_id`), so offering it while viewing someone else's badge
 * would misrepresent what the purchase does.
 */
export function BrandBadgeDialog({
  control,
  def,
  label,
}: {
  control: Dialog.DialogControlProps
  def: BrandBadgeDef
  label: string
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const {currentAccount} = useSession()
  const openLink = useOpenLink()

  const viewerHolds = badgesForDid(currentAccount?.did).some(
    b => b.slug === def.slug,
  )
  const checkoutUrl = viewerHolds
    ? undefined
    : badgePurchaseUrl(def, currentAccount?.did)

  return (
    <Dialog.Outer control={control} nativeOptions={{preventExpansion: true}}>
      <Dialog.ScrollableInner label={label} style={[web({maxWidth: 360})]}>
        <View style={[a.align_center, a.pb_md]}>
          <BadgeMark def={def} width={48} />
        </View>
        <View style={[a.align_center]}>
          <Text style={[a.text_lg, a.font_bold, a.pb_xs, t.atoms.text]}>
            {def.label}
          </Text>
          {def.description ? (
            <Text
              style={[
                a.leading_snug,
                a.text_center,
                a.pb_xl,
                a.text_md,
                t.atoms.text_contrast_high,
                {maxWidth: 320},
              ]}>
              {def.description}
            </Text>
          ) : (
            <View style={[a.pb_xl]} />
          )}
        </View>
        <View style={[a.w_full, a.gap_sm]}>
          {checkoutUrl && (
            <Button
              label={l`Get this badge`}
              onPress={() => {
                control.close(() => {
                  void openLink(checkoutUrl)
                })
              }}
              color="primary"
              size="large">
              <ButtonText>
                <Trans>Get this badge</Trans>
              </ButtonText>
            </Button>
          )}
          <Button
            label={l`Close`}
            onPress={() => control.close()}
            color={checkoutUrl ? 'secondary' : 'primary'}
            size="large">
            <ButtonText>
              <Trans>Close</Trans>
            </ButtonText>
          </Button>
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}
